import { useEffect, useRef, useState, type RefObject } from 'react';
import { initialInvoicingView, type InvoicingView, type InvoicingViewInputs } from '../utils/invoicingView';
import type { SubmitOutcome } from './useInvoiceSubmission';

export interface InvoicingViewState {
    view: InvoicingView | 'loading';
    showCreate: () => void;
    showPreviouslyInvoiced: () => void;
    onSubmitSettled: (outcome: SubmitOutcome) => void;
    previouslyInvoicedHeadingRef: RefObject<HTMLHeadingElement | null>;
    createHeadingRef: RefObject<HTMLHeadingElement | null>;
}

interface Options extends InvoicingViewInputs {
    agreementId: string | undefined;
    active: boolean;
    dismissNotice: () => void;
}

interface Decided {
    agreementId: string | undefined;
    view: InvoicingView | null;
}

export function useInvoicingView({ agreementId, active, dismissNotice, ...inputs }: Options): InvoicingViewState {
    const [decided, setDecided] = useState<Decided>({ agreementId, view: null });
    const previouslyInvoicedHeadingRef = useRef<HTMLHeadingElement | null>(null);
    const createHeadingRef = useRef<HTMLHeadingElement | null>(null);
    const focusTarget = useRef<Decided | null>(null);
    const canBrowseInvoicesRef = useRef(inputs.canBrowseInvoices);

    let current = decided;
    if (decided.agreementId !== agreementId || (!active && decided.view !== null)) {
        current = { agreementId, view: null };
        setDecided(current);
    }
    if (current.view === 'previously_invoiced' && !inputs.canBrowseInvoices) {
        current = { agreementId, view: 'create' };
        setDecided(current);
    }
    if (active && current.view === null) {
        const initial = initialInvoicingView(inputs);
        if (initial !== 'loading') {
            current = { agreementId, view: initial };
            setDecided(current);
        }
    }

    const view = active ? (current.view ?? 'loading') : 'loading';

    useEffect(() => {
        canBrowseInvoicesRef.current = inputs.canBrowseInvoices;
    }, [inputs.canBrowseInvoices]);

    useEffect(() => {
        if (focusTarget.current !== decided || view === 'loading') return;
        focusTarget.current = null;
        const heading = view === 'create' ? createHeadingRef : previouslyInvoicedHeadingRef;
        heading.current?.focus();
    }, [decided, view]);

    const switchTo = (next: InvoicingView): void => {
        if (next === view) {
            const heading = next === 'create' ? createHeadingRef : previouslyInvoicedHeadingRef;
            heading.current?.focus();
            return;
        }
        const target = { agreementId, view: next };
        focusTarget.current = target;
        setDecided(target);
    };

    return {
        view,
        showCreate: () => {
            dismissNotice();
            switchTo('create');
        },
        showPreviouslyInvoiced: () => switchTo('previously_invoiced'),
        onSubmitSettled: (outcome) => {
            if (outcome.ok && canBrowseInvoicesRef.current) switchTo('previously_invoiced');
        },
        previouslyInvoicedHeadingRef,
        createHeadingRef,
    };
}
